import { stockScreenerDescriptiveEn } from "../stockScreenerDescriptive.en";
import { backtesting } from "./backtesting";
import { investingCalculator } from "./investingCalculator";
import { purificationCalculator } from "./purificationCalculator";
import { zakatCalculator } from "./zakatCalculator";
import { shariahReport } from "./shariahReport";
import { staticPages } from "./staticPages";
import { articleSignupPrompt } from "./articleSignupPrompt";

export const pages = {
  common: {
    loading: "Loading...",
    pagination: {
      previous: "Previous",
      next: "Next",
    },
  },
  language: {
    ariaLabel: "Language",
  },
  stock: {
    seo: {
      title:
        "Is {{companyName}} - {{ticker}} stock halal and shariah compliant?",
      titleTickerOnly: "Is {{ticker}} stock halal and shariah compliant?",
      description:
        "Check if {{companyName}} ({{ticker}}) is halal and Shariah compliant. Akinda screening, ratios, and Shariah report.",
      keywords:
        "{{ticker}} halal stock, {{companyName}} Shariah compliant, islamic finance stock screening",
      descriptionWithStatus:
        "{{companyName}} ({{ticker}}) is classified as {{statusLabel}} by Akinda Shariah screening. View business activity, financial ratios, and full halal stock report.",
      introHeading: "Shariah compliance overview",
      introVerdict:
        "{{companyName}} ({{ticker}}) is currently classified as {{statusLabel}} by Akinda Shariah screening (AAOIFI-aligned methodology).",
      introVerdictUnknown:
        "Check whether {{companyName}} ({{ticker}}) is halal and Shariah compliant using Akinda screening, financial ratios, and business activity analysis.",
      introDisclaimer:
        "Screening is informational and not a fatwa. Consult a qualified scholar for personal guidance.",
    },
    shariahSummary: {
      purification: "Dividend purification",
      purificationHint: "Share of dividends to give to charity",
      limit: "Limit {{limit}}%",
      purificationCalculator: "Purification calculator",
    },
    summary: {
      aboutHeading: "About {{companyName}}",
      sectorIndustry:
        "{{companyName}} ({{ticker}}) operates in the {{sector}} sector, in the {{industry}} industry.",
      sectorOnly: "{{companyName}} ({{ticker}}) operates in the {{sector}} sector.",
      passesAll:
        "Why it is halal: it passes all three AAOIFI screens. Non-compliant revenue is {{business}} (limit {{businessLimit}}%), interest-bearing debt is {{debt}} (limit {{financialLimit}}%) and interest-bearing securities and assets are {{securities}} (limit {{financialLimit}}%).",
      failsScreens:
        "Why it is not classified as halal: it does not pass these screens: {{screens}}.",
      purification:
        "If you hold {{ticker}}, give {{pct}} of the dividends you receive to charity to purify the non-compliant share of its income.",
    },
    related: {
      heading: "Other halal stocks in {{sector}}",
      alternativesHeading: "Halal alternatives in {{sector}}",
    },
    // Page names in breadcrumbs (BreadcrumbList JSON-LD, src/seo/structuredData.ts).
    breadcrumbs: {
      HOME: "Home",
      ABOUT_US: "About",
      ABOUT_FOUNDER: "Founder",
      OUR_MISSION: "Our Mission",
      CONTACT: "Contact",
      COMMUNITY: "Community",
      FOR_BUSINESS: "For Business",
      MCP: "MCP Server",
      STARTUPS: "Startups",
      REFERRAL: "Referral Program",
      PRIVACY_POLICY: "Privacy Policy",
      TERMS_AND_CONDITIONS: "Terms & Conditions",
      DISCLOSURES: "Disclosures",
      SHARIAH_SCREENING_METHODOLOGY: "Shariah Screening Methodology",
      STOCK_SCREENER: "Stock Screener",
      ETF_SCREENER: "ETF Screener",
      STOCK_ANALYSES: "Stock Analyses",
      TOP_HALAL_STOCKS: "Top Halal Stocks",
      SCREENING: "Screening",
      INSIDER_BY_SECTOR: "Insider Trading by Sector",
      DIVIDEND: "Dividends",
      SUMMARY: "Market Summary",
      MARKET_NEWS: "Market News",
      READY_MADE_PORTFOLIO: "Ready-Made Portfolios",
      ARTICLES: "Articles",
      ACADEMY: "Academy",
      WEBINARS: "Webinars",
      INVESTING_CALCULATOR: "Investing Calculator",
      ZAKAT_CALCULATOR: "Zakat Calculator",
      PURIFICATION_CALCULATOR: "Purification Calculator",
      BACKTESTING: "Portfolio Backtesting",
      AKINDA_AI: "Akinda AI",
    },
    tabs: {
      summary: "Summary",
      rating: "Rating",
      ratings: "Ratings",
      forecast: "Forecast",
      financials: "Financials",
      shariahReport: "Shariah Report",
      valuation: "Valuation",
      ranking: "Ranking",
      swot: "SWOT",
      ownership: "Ownership",
      earnings: "Earnings",
      dividends: "Dividends",
      peers: "Peers",
    },
  },
  etfScreener: {
    title: "Halal ETF Screener",
    subtitle:
      "Filter the ETF universe by Shariah verdict, rulebook, where a fund trades, where it is domiciled, coverage, fees and size.",
    resultsCount: "{{count}} funds",
    empty: "No funds match these filters.",
    toolbar: {
      page: "Page {{current}} / {{total}}",
    },
    viewTabs: {
      weights: "Weights",
      rulebooks: "Rulebooks",
      fund: "Fund",
      basket: "Basket",
    },
    verdict: {
      halal: "Halal",
      doubtful: "Doubtful",
      notHalal: "Not halal",
      notRated: "Not rated by this method",
    },
    venues: { US: "US", GB: "UK", CA: "Canada" },
    venueNames: { US: "United States", GB: "United Kingdom", CA: "Canada" },
    singleHolding: "Single holding",
    singleHoldingHint: "This fund's basket is a single company, so its verdict is that company's.",
    fundScreenHint:
      "Akinda fund screen (AAOIFI-derived): a 5% limit on the fund's portfolio weight in holdings rated not halal.",
    coverageHint:
      "Coverage is the share of the fund's weight we hold a usable verdict for.",
    columns: {
      no: "No.",
      fund: "Fund",
      venue: "Venue",
      fundScreen: "Akinda fund screen",
      coverage: "Coverage",
      halalWeight: "Halal",
      doubtfulWeight: "Doubtful",
      notHalalWeight: "Not halal",
      notRatedWeight: "Not rated",
      rulebookDjim: "DJIM",
      rulebookFtse: "FTSE",
      rulebookMsci: "MSCI",
      rulebookSp: "S&P",
      expense: "Expense",
      aum: "AUM",
      issuer: "Issuer",
      holdings: "Holdings",
      basketAsOf: "Basket as of",
      lastUpdated: "Updated",
    },
    filters: {
      any: "Any",
      apply: "Apply",
      clear: "Clear",
      share: "Share",
      shareCopied: "Link copied to clipboard",
      shareFailed: "Could not copy the link",
      issuerPlaceholder: "e.g. iShares",
      searchPlaceholder: "Ticker or name",
      fields: {
        status: "Status",
        methodology: "Rulebook",
        venue: "Venue",
        domicile: "Domicile",
        coverage: "Coverage",
        expense: "Expense ratio",
        aum: "AUM",
        issuer: "Issuer",
        q: "Search",
      },
      methodologies: {
        aaoifi: "Akinda fund screen (AAOIFI)",
        djim: "DJIM",
        ftse: "FTSE",
        msci: "MSCI",
        sp: "S&P",
      },
      domiciles: {
        US: "United States",
        IE: "Ireland",
        CA: "Canada",
        LU: "Luxembourg",
        FR: "France",
        SE: "Sweden",
      },
      notes: {
        status: "Status filters the rulebook you pick. The Rulebooks tab shows all five side by side.",
        venue:
          "Venue is where a fund trades; domicile is where it is registered. London-listed funds are domiciled in Ireland or Luxembourg, so use Venue for UK ETFs.",
        expenseAum:
          "Expense ratio excludes funds with no fee listed. AUM compares each fund in its own reporting currency.",
      },
    },
    errors: {
      loadFailed: "Could not load ETF screener results",
      retry: "Retry",
    },
    seoContent: {
      showMore: "Show more",
      showLess: "Show less",
      helpTitle: "How Does Akinda Screen ETFs for Shariah Compliance?",
      helpParagraph1:
        "An ETF's verdict is decided by its basket, not its name. Akinda screens every holding as a company, weights each verdict by its share of the portfolio, and applies a 5% limit to the weight held in companies rated not halal.",
      helpParagraph2:
        "Filter funds by verdict and rulebook, by where they trade and where they are domiciled, by how much of the basket we can see, and by fees and fund size. Every fund also carries the four index rulebooks (DJIM, FTSE, MSCI and S&P), so you can compare standards side by side.",
      disagreeParagraph:
        "The fund screen and the four index rulebooks answer different questions, so they are expected to disagree. A fund can be doubtful on our fund screen and halal under S&P; both are correct.",
      coverageParagraph:
        "Coverage is the share of the fund's weight we hold a usable verdict for. Below 95%, a fund is rated only when the weight we can see already breaches the 5% limit; otherwise it is not rated by this method.",
      quickLinksTitle: "Quick links",
      links: {
        stockScreener: "Halal Stock Screener",
        topHalal: "Top Halal Stocks",
        readyMade: "Ready-Made Halal Portfolio",
        methodology: "Shariah Screening Methodology",
        forBusiness: "Halal ETF and Stock Data API for Business",
        academy: "Akinda Academy",
      },
      faqTitle: "Frequently Asked Questions",
      faqSubtitle: "How Akinda rates ETFs for Shariah compliance.",
      faq: {
        q1: {
          q: "How is an ETF's verdict decided?",
          a: "By its basket, not its name. We screen the fund's holdings, weight each constituent's verdict by its share of the portfolio, and apply a 5% limit to the weight held in companies we rate not halal. Nothing about the fund's marketing or its label enters the calculation.",
        },
        q2: {
          q: "Why do so few ETFs come out halal?",
          a: "The 5% limit applies to the combined weight of holdings rated not halal, and a broad-market fund typically holds far more than that in banks, insurers and other impermissible businesses. Funds built on Shariah indices or narrow sectors are the ones most likely to pass.",
        },
        q3: {
          q: "What does 'not rated by this method' mean?",
          a: "One of two things, and the data does not distinguish them: either we cannot see enough of the basket to answer responsibly (less than 95% of its weight, with no breach already visible in what we can see), or the fund is leveraged, inverse or long-short, so a weighted roll-up of its holdings would not describe it. In both cases we decline to publish a verdict rather than publish a weak one.",
        },
        q4: {
          q: "How can a fund be not halal when you can only see part of its basket?",
          a: "Because that verdict only moves one way. A fund is rated not halal when the holdings we can see already exceed the 5% limit on their own, and the part we cannot see can only add to that weight, never subtract from it. The reverse does not hold, which is why a fund we can see less than 95% of is never published as halal or doubtful.",
        },
        q5: {
          q: "Why do the five rulebooks disagree about the same fund?",
          a: "They are five different standards, each with its own thresholds and denominators, rolled up over the same basket. A fund can pass one and fail another, and both answers are correct. We publish all five rather than picking one.",
        },
        q6: {
          q: "How current is the data?",
          a: "Verdicts are recomputed every night. Each fund also carries the date of the basket it was screened against, because a provider's holdings file can itself be months old; we show that date rather than implying the basket is as fresh as the calculation.",
        },
      },
    },
  },
  etfDetail: {
    backToScreener: "← ETF screener",
    intro:
      "{{fundName}} ({{ticker}}) on the Akinda fund screen (AAOIFI-derived): {{statusLabel}}. Coverage {{coverage}}. Below: the fund under all five rulebooks, its facts and every holding with its own verdict.",
    methodologyLink: "How Akinda screens funds and stocks",
    seo: {
      title: "Is {{fundName}} ({{ticker}}) halal? ETF Shariah screening",
      titleTickerOnly: "Is {{ticker}} ETF halal? Shariah screening",
      description:
        "Shariah screening of {{fundName}} ({{ticker}}): Akinda fund screen verdict, coverage, the DJIM, FTSE, MSCI and S&P rulebooks, and every holding.",
      descriptionWithStatus:
        "{{fundName}} ({{ticker}}) is {{statusLabel}} on the Akinda fund screen (AAOIFI-derived). See coverage, the DJIM, FTSE, MSCI and S&P rulebooks, and every holding.",
      keywords:
        "{{ticker}} halal ETF, is {{ticker}} halal, {{fundName}} Shariah compliant, halal ETF screening",
    },
    verdict: {
      title: "Akinda fund screen",
      fundScreen: "Akinda fund screen (AAOIFI-derived)",
      barScaled: "The bar is scaled to fit; the figures are as reported, and the buckets do not always total 100%.",
    },
    rulebooks: {
      title: "Five rulebooks",
      rulebook: "Rulebook",
      verdict: "Verdict",
      split: "Split (0–100%)",
      scaleNote:
        "Every bar uses the same 0–100% scale. Index rulebooks report only halal and not-halal weight; uncoloured weight is weight that rulebook does not classify. Coverage is published for the Akinda fund screen only.",
    },
    facts: {
      title: "Fund facts",
      issuer: "Issuer",
      venue: "Venue (where it trades)",
      domicile: "Domicile (where it is registered)",
      currency: "Reporting currency",
      expense: "Expense ratio",
      aum: "AUM",
      holdings: "Holdings",
      holdingsValue: "{{total}} ({{screened}} screened)",
      basketAsOf: "Basket as of (provider)",
      basketFetched: "Basket fetched by Akinda",
      lastUpdated: "Verdict updated",
    },
    holdings: {
      title: "Holdings",
      holding: "Holding",
      weight: "Weight",
      status: "Akinda (AAOIFI)",
      notRated: "Not rated",
      range: "{{from}}–{{to}} of {{total}} holdings, largest first",
      asOf: "basket as of {{date}}",
      empty: "No holdings are on file for this fund yet.",
      loadFailed: "Could not load this fund's holdings.",
      notRatedNote:
        "Not rated means we hold no usable verdict for that company; it is never treated as compliant.",
    },
    errors: {
      notFoundTitle: "{{ticker}}: this fund is not in our coverage",
      notFound: "Search the ETF screener for funds we rate.",
      loadFailed: "Could not load this fund.",
      tryAgain: "Please try again in a moment.",
    },
  },
  stockScreener: {
    title: "Halal Stock Screener",
    subtitle:
      "Filter the AAOIFI-compliant universe by descriptive, fundamental, and technical criteria. Find Shariah-compliant stocks globally.",
    resultsCount: "{{count}} stocks",
    toolbar: {
      page: "Page {{current}} / {{total}}",
      loadingMore: "loading more",
      filters: "Filters",
    },
    status: {
      halal: "Halal",
      doubtful: "Doubtful",
      notHalal: "Not Halal",
      notQualified: "Not qualified",
    },
    columns: {
      no: "No.",
      symbol: "Ticker",
      company: "Company",
      sector: "Sector",
      industry: "Industry",
      country: "Country",
      status: "Status",
      price: "Price",
      change: "Chg %",
      volume: "Volume",
      marketCap: "Mkt Cap",
      target: "Target",
      week52High: "52W H",
      week52Low: "52W L",
      rating: "Rating",
      dividendYield: "Div Yield",
    },
    viewTabs: {
      core: "Core",
      valuation: "Valuation",
      profitability: "Profitability",
      growth: "Growth",
      performance: "Performance",
      technical: "Technical",
      dividends: "Dividends",
    },
    filters: {
      any: "Any",
      halalOnly: "Halal",
      expand: "Show filters",
      collapse: "Hide filters",
      clear: "Clear",
      apply: "Apply",
      share: "Share",
      shareCopied: "Filter link copied to clipboard",
      shareFailed: "Could not copy link",
      tabs: {
        descriptive: "Descriptive",
        fundamental: "Fundamental",
        technical: "Technical",
        all: "All",
      },
      groups: {
        descriptive: "Filters",
        ranking: "Akinda ranking score",
      },
      fields: {
        sector: "Sector",
        industry: "Industry",
        market_cap: "Market Cap",
        volume: "Volume",
        avg_volume: "Avg Vol",
        shares_out: "Shares Out",
        float: "Float",
        price: "Price $",
        halal_rating: "Halal Rating",
        akinda_ranking: "Akinda Ranking",
        profitability: "Profitability",
        liquidity: "Liquidity",
        financial_strength: "Financial Strength",
        value: "Value",
        growth: "Growth",
        momentum: "Momentum",
        pe: "P/E",
        p_cash: "P/Cash",
        roe: "ROE",
        quick_ratio: "Quick R",
        impure_ratio: "Impure",
        forward_pe: "Fwd P/E",
        p_free_cash_flow: "P/FCF",
        roa: "ROA",
        current_ratio: "Curr R",
        peg: "PEG",
        dividend_yield: "Div Yield",
        gross_margin: "Gross M",
        payout_ratio: "Payout",
        ps: "P/S",
        eps_growth_this_year: "EPS Gr Y",
        profit_margin: "Net Margin",
        debt_ratio: "Debt R",
        pb: "P/B",
        eps_growth_next_year: "EPS nY",
        oper_margin: "Oper M",
        liquidity_ratio: "Liq R",
        rsi_14: "RSI (14)",
        beta: "Beta",
        change_perc: "Change%",
        gap_perc: "Gap%",
        relative_volume: "Rel Vol",
        perf_week: "Perf W",
        perf_month: "Perf M",
        perf_ytd: "Perf YTD",
        perf_year: "Perf Y",
      },
      descriptive: stockScreenerDescriptiveEn,
      thresholds: {
        percent: {
          positive: "Positive (>0%)",
          negative: "Negative (<0%)",
          o90: "Over 90%",
          o80: "Over 80%",
          o70: "Over 70%",
          o60: "Over 60%",
          o50: "Over 50%",
          o40: "Over 40%",
          o30: "Over 30%",
          o20: "Over 20%",
          o10: "Over 10%",
          o5: "Over 5%",
          u10: "Under 10%",
          u5: "Under 5%",
        },
        ratio: {
          u5: "Under 5",
          u10: "Under 10",
          u15: "Under 15",
          u20: "Under 20",
          o5: "Over 5",
          o10: "Over 10",
          o15: "Over 15",
          o20: "Over 20",
          o25: "Over 25",
          o30: "Over 30",
          o50: "Over 50",
        },
        rsi: {
          o80: "Over 80",
          o70: "Over 70",
          o60: "Over 60",
          o50: "Over 50",
          o40: "Over 40",
          o30: "Over 30",
          o20: "Over 20",
          u20: "Under 20",
          u30: "Under 30",
          u40: "Under 40",
        },
      },
    },
    seoContent: {
      showMore: "Show more",
      showLess: "Show less",
      helpTitle: "How Can Akinda Help You Find Shariah-Compliant Stocks?",
      helpParagraph1:
        "Finding Shariah-compliant stocks can be complex when you need to review both business activities and financial ratios under Islamic principles. Akinda's halal stock screener simplifies that work by filtering the investable universe using AAOIFI-aligned standards, so you can avoid prohibited sectors such as alcohol, gambling, and conventional interest-based finance without reading every annual report by hand.",
      helpParagraph2:
        "Beyond compliance status, you can refine results with descriptive, fundamental, and technical filters—sector, market cap, valuation, profitability, momentum, and more—then review halal ratings and company details in one dense table. Whether you are building your first halal portfolio or monitoring existing holdings, Akinda helps you discover opportunities faster with transparent screening and research tools built for Muslim investors worldwide.",
      exploreTitle: "Explore Halal Investing with Akinda",
      exploreParagraph:
        "Use the screener together with our research, portfolios, and education to stay aligned with your values from discovery through ongoing monitoring.",
      quickLinksTitle: "Quick links",
      links: {
        topHalal: "Top Halal Stocks",
        stockAnalyses: "Stock Analyses",
        readyMade: "Ready-Made Halal Portfolio",
        methodology: "Shariah Screening Methodology",
        forBusiness: "Halal Stock Data API for Business",
        academy: "Akinda Academy",
      },
      popularTitle: "Popular Halal Stock Lookups",
      popularStock: "Is {{symbol}} a halal stock?",
      faqTitle: "Frequently Asked Questions",
      faqSubtitle:
        "Answers about Akinda's free halal stock screener and how we help you invest in line with Shariah principles.",
      faq: {
        q1: {
          q: "What is a halal stock screener?",
          a: "A halal stock screener is a tool that filters listed companies to show which ones meet Shariah investment criteria. Akinda's screener checks business activity screens and financial ratio thresholds aligned with AAOIFI guidance, then displays compliance status, sector, fundamentals, and Akinda halal ratings so you can compare stocks quickly.",
        },
        q2: {
          q: "How can I find halal stocks easily?",
          a: "Start with the Halal filter to focus on compliant names, then add sector, industry, market cap, or ranking filters and click Apply. You can share your filter set via link, switch table views for valuation or technical metrics, and open any ticker for a full Shariah report and AI-assisted analysis on Akinda.",
        },
        q3: {
          q: "Are halal stocks available worldwide?",
          a: "Akinda covers a broad global equity universe screened for Shariah compliance. Availability of individual tickers depends on our data coverage and your subscription. Use Top Halal Stocks and Stock Analyses to explore featured names, or filter the screener to narrow by sector and fundamentals.",
        },
        q4: {
          q: "What criteria define halal stocks on Akinda?",
          a: "Stocks pass activity-based screens that exclude impermissible revenue sources, then financial screens on debt, interest income, and other ratio limits consistent with common AAOIFI-style methodologies. Each company receives a halal status and rating so you can see not only pass/fail but relative quality within the compliant set.",
        },
        q5: {
          q: "Why should I use Akinda's halal stock screener?",
          a: "Manual screening is slow and easy to get wrong. Akinda combines automated Shariah screening with Finviz-style filters, halal ratings, and links to deep stock reports—so you save time, reduce compliance risk, and make better-informed halal investment decisions in one platform.",
        },
      },
    },
    errors: {
      loadFailed: "Could not load screener results",
      forbidden: "Your plan does not include screener access.",
      signInTitle: "Sign in to see screener results",
      signInDescription:
        "Create a free account or sign in to filter Shariah-compliant stocks.",
      signIn: "Sign in",
      retry: "Retry",
    },
  },
  topHalal: {
    title: "Top Halal Stocks",
    subtitle:
      "Browse top halal stocks screened for Shariah compliance. Compare performance and valuation to find ethical investments.",
    allSectors: "All Sectors",
    addToPortfolio: "Add to Portfolio",
    addToPortfolioWithSymbol: "Add {{symbol}} to your portfolio",
    addToPortfolioSubtitle: "Add stock to your portfolio",
    cancel: "Cancel",
    done: "Done",
    createPortfolio: "Create Portfolio",
    symbol: "Symbol",
    portfolio: "Portfolio",
    selectPortfolio: "Select portfolio",
    quantity: "Quantity",
    enterQuantity: "Enter quantity",
    noPortfoliosFound: "No portfolios found",
    loadingPortfolios: "Loading portfolios...",
    portfolioRequired: "Portfolio is required",
    quantityRequired: "Quantity is required",
    quantityMin: "Quantity must be greater than 0",
    noPortfoliosYet: "You don't have any portfolios yet.",
    createPortfolioHint: "Create a portfolio to add stocks to your watchlist.",
    addSuccess: "Stock added to {{portfolio}} successfully. Click to view.",
    addError: "Failed to add stock to portfolio.",
    defaultPortfolioName: "portfolio",
    errorLoading: "Error loading stocks: {{message}}",
    authPrompt: {
      title: "See more halal stocks",
      subtitle:
        "Create a free account or sign in to browse the rest of the list.",
    },
    columns: {
      company: "Company",
      sector: "Sector",
      marketCap: "Market cap",
      price: "Price",
      change: "Change",
      targetPrice: "Target price",
      pe: "P/E",
      halalStatus: "Halal Status",
      ranking: "Ranking",
    },
    sectors: {
      basicMaterials: "Basic Materials",
      communicationServices: "Communication Services",
      consumerCyclical: "Consumer Cyclical",
      consumerDefensive: "Consumer Defensive",
      energy: "Energy",
      financialServices: "Financial Services",
      healthcare: "Healthcare",
      industrials: "Industrials",
      realEstate: "Real Estate",
      technology: "Technology",
      utilities: "Utilities",
    },
    halalStatus: {
      halal: "Halal",
      notHalal: "Not halal",
      doubtful: "Doubtful",
      notQualified: "Not qualified",
    },
    info: {
      keyStats: {
        title: "Key Stats",
        titleShort: "Key stats",
        metric: "Metric",
        value: "Value",
        noData: "No data available",
        na: "N/A",
        labels: {
          todayRange: "Today's Range",
          fiftyDayRange: "50-Day Range",
          fiftyTwoWeekRange: "52-Week Range",
          volume: "Volume",
          averageVolume: "Average Volume",
          marketCapitalization: "Market Capitalization",
          peRatio: "P/E Ratio",
          dividendYield: "Dividend Yield",
          priceTarget: "Price Target",
          consensusRating: "Consensus Rating",
        },
      },
      swot: {
        title: "SWOT Analysis",
        selectCategory: "Select category",
        viewAll: "View all",
        noCategoryData: "No {{category}} data available",
        categories: {
          strengths: "Strength",
          weaknesses: "Weakness",
          opportunities: "Opportunity",
          threats: "Threats",
        },
      },
      ratingHistory: {
        title: "Rating History",
        analyst: "Analyst",
        firm: "Firm",
        rating: "Rating",
        action: "Action",
        previousGrade: "Previous Grade",
        priceWhenPosted: "Price When Posted",
        date: "Date",
        unknown: "Unknown",
        pageOf: "Page {{page}} of {{totalPages}}",
        errorLoading: "Error loading rating history: {{message}}",
        noData: "No rating history available",
        unableRefresh: "Unable to refresh rating history: {{message}}",
      },
      topFundHolders: {
        title: "Top Fund Holders",
        guru: "Guru",
        currentShares: "Current Shares",
        sharesOutstanding: "% of Shares Outstanding",
        currentValue: "Current Value",
        filingDate: "Filing Date",
        source: "Source",
        viewFiling: "View Filing",
        holdersTotal: "{{from}}-{{to}} of {{total}} holders",
        errorLoading: "Error loading fund holders: {{message}}",
        noData: "No fund holder data available",
      },
      dividends: {
        payoutHistory: {
          title: "Dividend Payout History",
          year: "Year",
          amount: "Amount",
          adjAmount: "Adj. Amount",
          dividendType: "Dividend Type",
          frequency: "Frequency",
          exDivDate: "Ex-Div Date",
          recordDate: "Record Date",
          regular: "Regular",
          special: "Special",
          recordsTotal: "{{from}}-{{to}} of {{total}} dividend records",
          errorLoading: "Error loading dividend payout history: {{message}}",
          noData: "No dividend payout history available",
        },
        consensusEstimates: {
          title: "Consensus Dividend Estimates",
          fiscalPeriodEnding: "Fiscal Period Ending",
          consensusRate: "Consensus Rate",
          consensusYield: "Consensus Yield",
          low: "Low",
          high: "High",
          analystsCount: "# of Analysts",
          errorLoading: "Error loading dividend estimates: {{message}}",
          noData: "No dividend estimates available",
        },
      },
      stockForecast: {
        title: "{{symbol}} Stock Forecast",
        target: "Target",
        low: "Low",
        average: "Average",
        median: "Median",
        high: "High",
        price: "Price",
        change: "Change",
        historical: "Historical",
        failedLoad: "Failed to load stock forecast data: {{message}}",
        noData: "No stock forecast data available.",
      },
      rankingTables: {
        name: "Name",
        current: "Current",
        score: "Score",
        loadingGF: "Error loading GF value data: {{message}}",
        loadingFinancialStrength:
          "Error loading financial strength data: {{message}}",
        loadingGrowth: "Error loading growth data: {{message}}",
        loadingLiquidity: "Error loading liquidity data: {{message}}",
        loadingRanking: "Error loading ranking data: {{message}}",
        loadingDividend: "Error loading dividend data: {{message}}",
        loadingProfitability: "Error loading profitability data: {{message}}",
        loadingMomentum: "Error loading momentum data: {{message}}",
        gfTitle: "GF Value Rank",
        gfTitleWithScore: "GF Value Rank: {{score}}/10",
        financialStrengthTitle: "Financial Strength",
        financialStrengthTitleWithScore: "Financial Strength: {{score}}/10",
        growthTitle: "Growth Rank",
        growthTitleWithScore: "Growth Rank: {{score}}/10",
        liquidityTitle: "Liquidity Ratio",
        liquidityTitleWithScore: "Liquidity Ratio: {{score}}/10",
        akindaTitle: "Akinda ranking score",
        akindaTitleWithScore: "Akinda ranking score: {{score}}/10",
        dividendTitle: "Dividend & Buy Back score",
        dividendTitleWithScore: "Dividend & Buy Back score: {{score}}/10",
        profitabilityTitle: "Profitability Rank",
        profitabilityTitleWithScore: "Profitability Rank: {{score}}/10",
        momentumTitle: "Momentum Rank",
        momentumTitleWithScore: "Momentum Rank: {{score}}/10",
      },
      earnings: {
        latestTitle: "Latest Quarter's Earnings",
        upcomingTitle: "Upcoming Quarter's Earnings",
        label: "Label",
        value: "Value",
        labels: {
          announceDate: "Announce Date",
          epsNormalizedActual: "EPS Normalized Actual",
          epsGaapActual: "EPS GAAP Actual",
          revenueActual: "Revenue Actual",
          revenueSurprise: "Revenue Surprise",
          epsNormalizedEstimate: "EPS Normalized Estimate",
          epsGaapEstimate: "EPS GAAP Estimate",
          revenueEstimate: "Revenue Estimate",
          epsRevisions: "EPS Revisions",
          beatByAmount: "Beat by {{value}}",
          beatByBracket: "(Beat by {{value}})",
        },
        transcripts: {
          title: "({{symbol}}) Transcripts",
          transcriptTitle: "{{symbol}} Earnings Call Transcript",
          transcriptTitleWithPeriod:
            "{{symbol}} Earnings Call Transcript ({{period}} {{year}})",
          viewMore: "View more",
          failedLoad: "Failed to load transcripts: {{message}}",
          noData: "No transcripts available.",
          modalTitle: "({{symbol}}) Earnings Call Transcript",
          date: "Date",
          period: "Period",
        },
        epsSurprise: {
          loading: "Loading earnings data...",
          errorLoading: "Error loading earnings data: {{message}}",
          noData: "No earnings data available.",
        },
        epsQuarterChart: {
          title: "EPS Surprise & Estimates by Quarter",
          epsActual: "EPS Actual",
          epsEstimated: "EPS Estimated",
        },
      },
      ownership: {
        selectYear: "Select year",
        filingsColumns: {
          document: "Document",
          filingDate: "Filing Date",
          filingDatePrice: "Filing date price",
          documentDate: "Document Date",
        },
        recentFilings: "Recent Filings",
        financialsSection: "Financials",
        topDirectHolders: "Top Direct Holders",
        holder: "Holder",
        position: "Position",
        shares: "Shares",
        reportDate: "Report date",
        errorDirectHolders: "Error loading direct holders: {{message}}",
        noDirectHolders: "No direct holder data available",
        type: "Type",
        commonStockHeld: "Common Stock Held",
        pctSharesOutstanding: "% of shares outstanding",
        marketValue: "Market value",
        composition: "Composition",
        stockOwnershipTitle: "{{symbol}} Ownership",
      },
      demoAnalysis: {
        title: "{{symbol}} Analysis",
        viewAll: "View all",
        commentsCount: "{{count}} comments",
        recBuy: "Buy",
        recStrongBuy: "Strong Buy",
        recHold: "Hold",
        recSell: "Sell",
        title1: "eBay: Unmissable Offers This Week",
        title2: "Walmart: Deals You Can't Ignore",
        title3: "Target: Special Discounts Just Launched",
        title4:
          "Amazon: The Whole Package, Long-Term Buy For This Tough Grader (Upgrade)",
        title5: "Alibaba: Exclusive Shopping Events",
        title6: "Newegg: Hot Tech Deals This Month",
        cat1: "Market Insights",
        cat2: "Retail Trends",
        cat3: "Consumer News",
        cat4: "Tech Review",
        cat5: "Global Market",
        cat6: "Gadget Guide",
        date1: "Sun, June 01",
        date2: "Mon, June 02",
        date3: "Tue, June 03",
        date4: "Wed, June 04",
        date5: "Thu, June 05",
        date6: "Fri, June 06",
      },
      companyProfile: {
        title: "Company Profile",
        loading: "Loading...",
        noData: "No data available",
        noDescription: "No description available",
        sector: "Sector",
        employees: "Employees",
        industry: "Industry",
        founded: "Founded",
        website: "Website",
        phoneNumber: "Phone Number",
        address: "Address",
      },
      filings: {
        title: "{{company}} Filings",
        filingDate: "Filing date",
        documentDate: "Document date",
        form: "Form",
        form4: "Form 4",
        others: "Others",
      },
      recommendCard: {
        failedLoad: "Failed to load financial analytics.",
        noData: "No financial analytics data available.",
        openShariahReport: "Open Shariah report tab",
      },
      forecastDescription: {
        failedLoad: "Failed to load analyst description: {{message}}",
        noData: "No analyst description data available.",
        stockPriceForecast: "Stock Price Forecast",
        priceTarget: "Price Target:",
        stockPrice: "Stock Price",
      },
      dividendHistory: {
        title: "Dividend History",
        loading: "Loading dividend history...",
        errorLoading: "Error loading dividend history: {{message}}",
        noData: "No dividend history available",
        legendDividend: "Dividend",
      },
      priceDividend: {
        title: "Price & Dividend History",
        loading: "Loading price history...",
        range12m: "12 months",
        range30d: "30 days",
        range7d: "7 days",
        range24h: "24 hours",
      },
      dividendVsEstimate: {
        title: "Dividend Actual vs Estimate",
        actual: "Dividend Actual",
        estimate: "Dividend Estimate",
      },
      dividendChart: {
        loading: "Loading dividend data…",
        failedLoad: "Failed to load dividend data: {{message}}",
        unknownError: "Unknown error",
        noData: "No dividend data available.",
      },
      priceDividendHistory: {
        errorLoading: "Error loading price history: {{message}}",
        noData: "No price history available",
      },
      peers: {
        symbol: "Symbol",
        annualizedReturnTitle: "Annualized Return %",
        balanceSheetTitle: "Balance Sheet",
        cashFlowTitle: "Cash Flow Statement",
        dividendsTitle: "Dividends",
        incomeStatementTitle: "Income Statement",
        failedAnnualizedReturn:
          "Failed to load annualized return data: {{message}}",
        failedBalanceSheet: "Failed to load balance sheet data: {{message}}",
        failedCashFlow: "Failed to load cash flow data: {{message}}",
        failedDividends: "Failed to load dividends data: {{message}}",
        failedIncomeStatement:
          "Failed to load income statement data: {{message}}",
        noAnnualizedReturn: "No annualized return data available.",
        noBalanceSheet: "No balance sheet data available.",
        noCashFlow: "No cash flow data available.",
        noDividends: "No dividends data available.",
        noIncomeStatement: "No income statement data available.",
        periods: {
          week1: "1 Week",
          month1: "1 Month",
          months3: "3 Months",
          months6: "6 Months",
          ytd: "YTD",
          year1: "1 Year",
          years3: "3 Years",
          years5: "5 Years",
          years10: "10 Years",
        },
        profileTitle: "Profile",
        failedProfile: "Failed to load profile data: {{message}}",
        noProfile: "No profile data available.",
        profileCompanyName: "Company Name",
        akindaRankingTitle: "Akinda Ranking Score",
        failedRankingScore: "Failed to load ranking score data: {{message}}",
        noRankingScore: "No ranking score data available.",
        scoreProfitability: "Profitability",
        scoreLiquidityRatio: "Liquidity ratio",
        scoreFinancialStrength: "Financial strength",
        scoreValue: "Value",
        scoreGrowth: "Growth",
        scoreMomentum: "Momentum",
        scoreDividend: "Dividend",
      },
      valuation: {
        title: "Valuation Ratios",
        failed: "Error loading valuation ratios: {{message}}",
        metrics: {
          priceToBookValue: "Price to Book Value",
          priceToTangibleBookValue: "Price to Tangible Book Value",
          priceToRevenue: "Price to Revenue",
          priceToEarnings: "Price to Earnings",
          dividendYield: "Dividend Yield",
          earningsYield: "Earnings Yield",
          enterpriseValueToInvestedCapital:
            "Enterprise Value to Invested Capital",
          enterpriseValueToRevenue: "Enterprise Value to Revenue",
          enterpriseValueToEBITDA: "Enterprise Value to EBITDA",
          enterpriseValueToOperatingCashFlow:
            "Enterprise Value to Operating Cash Flow",
          enterpriseValueToFreeCashFlow: "Enterprise Value to Free Cash Flow",
        },
      },
      dividendSummary: {
        title: "Dividend Summary",
        divYieldFwd: "Div Yield (FWD)",
        annualPayoutFwd: "Annual Payout (FWD)",
        payoutRatio: "Payout Ratio",
        fiveYearGrowthRate: "5 Year Growth Rate",
        failed: "Error loading dividend summary: {{message}}",
        noData: "No dividend data available",
      },
      financials: {
        metric: "Metric",
        fyPeriod: "FY {{period}}",
        unknownPeriod: "Unknown",
        errorLoading: "Error loading {{report}}: {{message}}",
        reportTypes: {
          income: "Income",
          balanceSheet: "Balance Sheet",
          cashFlow: "Cash Flow",
          ratios: "Ratios",
        },
        periods: {
          annual: "Annual",
          quarterly: "Quarterly",
        },
        placeholders: {
          reportType: "Select report type",
          period: "Select period",
        },
        subtitle: {
          withFiscal:
            "Financials in millions {{currency}}. {{fiscalDescription}}.",
          default:
            "Financials in millions {{currency}}. Fiscal year is January - December.",
        },
        headings: {
          income: "{{name}} Income Statement",
          balanceSheet: "{{name}} Balance Sheet",
          cashFlow: "{{name}} Cash Flow Statement",
          ratios: "{{name}} Ratios",
        },
        labels: {
          fiscalYear: "Fiscal Year",
          periodEnding: "Period Ending",
          cashAndEquivalents: "Cash & equivalents",
          shortTermInvestment: "Short - Term investment",
          cashAndShortTermInvestment: "Cash - short term investment",
          cashGrows: "Cash grows",
          accountsReceivable: "Accounts receivable",
          otherReceivable: "Other receivable",
          receivable: "Receivable",
          inventory: "Inventory",
          prepaidExpenses: "Prepaid expenses",
          otherCurrentAssets: "Other current Assets",
          totalCurrentAssets: "Total current assets",
          netIncome: "Net income",
          depreciationAndAmortization: "Depreciation & Amortization",
          assetWritedownRestructuringCosts:
            "Asset Writedown & Restructuring Costs",
          stockBasedCompensation: "Stock-Based Compensation",
          otherOperatingActivities: "Other Operating Activities",
          changeInAccountsReceivable: "Change in Accounts Receivable",
          changeInInventory: "Change in Inventory",
          changeInAccountsPayable: "Change in Accounts Payable",
          changeInUnearnedRevenue: "Change in Unearned Revenue",
          changeInOtherNetOperatingAssets:
            "Change in Other Net Operating Assets",
          operatingCashFlow: "Operating Cash Flow",
          marketCapitalization: "Market Capitalization",
          marketCapGrowth: "Market Cap Growth",
          enterpriseValue: "Enterprise Value",
          lastClosePrice: "Last Close Price",
          peRatio: "PE Ratio",
          forwardPe: "Forward PE",
          psRatio: "PS Ratio",
          pbRatio: "PB Ratio",
          ptbvRatio: "P/TBV Ratio",
          pfcfRatio: "P/FCF Ratio",
          pocfRatio: "P/OCF Ratio",
          revenue: "Revenue",
          revenueGrowthYoy: "Revenue Growth (YoY)",
          costOfRevenue: "Cost of Revenue",
          grossProfit: "Gross Profit",
          sellingGeneralAdmin: "Selling, General & Admin",
          researchDevelopment: "Research & Development",
          otherOperatingExpenses: "Other Operating Expenses",
          operatingExpenses: "Operating Expenses",
          operatingIncome: "Operating Income",
          interestExpense: "Interest Expense",
          interestInvestmentIncome: "Interest & Investment Income",
          revenueGrowsShort: "Revenue Grows",
          researchDevelopmentComma: "Research, Development",
          interestExpensePlural: "Interest Expenses",
          interestIncomeInvest: "Interest Invest Income",
          dividend: "Dividend",
          tableYield: "Yield",
          annualPayout: "Annual Payout",
        },
      },
      shariahReport,
    },
  },
  akindaAi: {
    pageTitle: "Ai analysis",
  },
  games: {
    pageTitle: "Investing games",
  },
  backtesting,
  readyMadePortfolio: {
    pageTitle: "Ready-made Portfolio",
    holdingsTitle: "Portfolio holdings",
    subtitle:
      "Expert-curated Sharia-compliant portfolios you can follow and replicate.",
    createPortfolio: "Create portfolio",
    edit: "Edit",
    emptyState: "No ready-made portfolios have been published yet.",
    loadError: "Unable to load ready-made portfolios. Please try again.",
    createSuccess: "Ready-made portfolio created successfully.",
    updateSuccess: "Ready-made portfolio updated successfully.",
    backToGallery: "Back to gallery",
    notFoundTitle: "Portfolio not found",
    notFoundBody:
      "This ready-made portfolio is unavailable or is no longer published.",
    emptyHoldings: "This portfolio does not have any stocks yet.",
    manageStocks: "Manage stocks",
    builtFrom: "Built from filter",
    lastSaved: "Holdings saved {{date}}",
    stocks: {
      back: "Back to portfolio",
      subtitle:
        "Filter stocks, switch on the ones to hold, set quantities, then preview and save.",
      saveSuccess: "Holdings saved.",
      saveFailed: "Could not save holdings.",
      missingQuantity: "Enter a quantity for: {{tickers}}",
      shortfall: "Not enough cash for the planned buys. Add cash or lower quantities.",
      tooMany: "At most {{max}} stocks can be saved at once.",
      unpriced: "No price for: {{tickers}}. Their amounts are not in the estimate.",
      cash: {
        available: "Cash available",
        sells: "Planned sells",
        buys: "Planned buys",
        after: "Cash after",
        shortfall: "Still needed",
        addCash: "Add cash",
      },
      selection: {
        title: "Selection",
        hint: "Switched-off holdings are sold on save. New picks need a quantity.",
        holdings: "Holdings",
        newPicks: "New picks",
        noHoldings: "No holdings yet.",
        noPicks: "Switch on stocks in the results to add them.",
        notInFilter: "Not in filter",
        willSell: "Will be sold",
        willSellCount: "{{count}} will be sold",
        new: "New",
        heldQty: "held {{qty}}",
        quantity: "Quantity",
      },
      results: {
        noFilter: "Apply a filter to see stocks.",
        addAllPage: "Add all on this page",
        amountPerStock: "Amount per new stock",
        fill: "Fill quantities",
        count: "{{count}} matches",
        checking: "checking holdings…",
      },
      columns: {
        inPortfolio: "Hold",
        quantity: "Quantity",
      },
      preview: {
        button: "Preview",
        title: "Preview trades",
        subtitle: "These trades run at live prices when you save.",
        side: "Side",
        ticker: "Ticker",
        reason: "Reason",
        quantity: "Quantity",
        price: "Price",
        amount: "Amount",
        cashBefore: "Cash before",
        cashAfter: "Cash after",
        equityBefore: "Equity before",
        equityAfter: "Equity after",
        cashNeeded: "Add {{amount}} before saving.",
        nothing: "Nothing to change.",
        skipped: "Skipped, no price: {{tickers}}",
        save: "Save ({{count}} trades)",
        cancel: "Cancel",
        failed: "Preview failed.",
        sellAllTitle: "Sell every holding?",
        sellAllBody: "No stock is switched on. Saving will sell every holding.",
        sellAllConfirm: "Continue",
        reasons: {
          added: "New",
          removed: "Removed",
          increased: "Increase",
          decreased: "Decrease",
        },
      },
      deposit: {
        title: "Add cash",
        subtitle: "Current cash: {{cash}}",
        amount: "Amount",
        submit: "Add",
        cancel: "Cancel",
        success: "Cash added.",
        failed: "Could not add cash.",
      },
    },
    follow: {
      follow: "Follow",
      following: "Following",
      followers_one: "{{count}} follower",
      followers_other: "{{count}} followers",
      hint: "get a notification on every trade",
      followed: "You now follow this portfolio. We will notify you when it changes.",
      unfollowed: "You no longer follow this portfolio.",
      failed: "Could not update follow. Try again.",
    },
    performance: {
      title: "Performance",
      sinceLabel: "{{value}} since {{date}}",
      growthOf1000: "Growth of $1,000",
      tooltipReturn: "Return",
      empty: "Performance appears after the first trade.",
      loadError: "Could not load performance.",
      methodology:
        "Price return, time-weighted: cash added by the team does not affect the chart. Past performance is not a guarantee of future results.",
      priceReturnNote: "Price return, time-weighted. Past performance does not guarantee future results.",
      gapsNote: "Some days use the purchase price where a market price was unavailable.",
      ranges: { "1w": "1W", "1m": "1M", "3m": "3M", "6m": "6M", all: "ALL" },
    },
    tabs: {
      holdings: "Holdings",
      history: "Trade history",
    },
    history: {
      date: "Date",
      side: "Side",
      ticker: "Ticker",
      quantity: "Quantity",
      price: "Price",
      amount: "Amount",
      empty: "No trades yet.",
      loadError: "Could not load the trade history.",
      sides: { BUY: "Buy", SELL: "Sell", DEPOSIT: "Deposit" },
    },
    authPrompt: {
      title: "Open this portfolio",
      subtitle:
        "Create a free account or sign in to view this ready-made portfolio.",
    },
  },
  stockAnalyses: {
    title: "Investing Signals",
    all: "All",
    selectCategory: "Select category",
    addStock: "Add stock",
    updated: "Updated: {{date}}",
    deleteTitle: "Delete Stock Analysis",
    deleteDescription: "Are you sure you want to delete this stock analysis?",
    yes: "Yes",
    no: "No",
    delete: "Delete",
    edit: "Edit",
    report: "Report",
    deleteSuccess: "Stock analysis deleted successfully",
    deleteError: "Failed to delete stock analysis",
    errorLoading: "Error loading stock analyses: {{message}}",
    paginationSummary: "{{from}}-{{to}} of {{total}} stock analyses",
    columns: {
      company: "Company",
      sector: "Sector",
      ticker: "Ticker",
      givenBy: "Given By",
      category: "Category",
      signal: "Signal",
      date: "Date",
      update: "Update",
      updated: "Updated",
      result: "Result",
    },
    categories: {
      investors: "Investors",
      bigStocks: "Big Stocks",
      cheapStocks: "Cheap Stocks",
    },
    pagination: {
      previous: "Previous",
      next: "Next",
    },
    stats: {
      title: "Track record",
      periods: { "3m": "3M", "6m": "6M", "12m": "12M", all: "All" },
      winRate: "Win rate",
      closedOf: "{{wins}} of {{closed}} closed signals",
      noClosed: "No closed signals yet",
      targets: "Targets reached",
      take1: "Take 1",
      take2: "Take 2",
      take3: "Take 3",
      stoploss: "Stoploss",
      streak: "Current streak",
      streakWins_one: "{{count}} win",
      streakWins_other: "{{count}} wins",
      streakLosses_one: "{{count}} loss",
      streakLosses_other: "{{count}} losses",
      bestStreak: "Best run: {{count}} wins in a row",
      open: "Open signals",
      avgDays: "Avg. {{days}} days to close",
      definition:
        "A win is a signal that reached Take 1, 2 or 3. A loss hit its stoploss. Open signals are not counted in the win rate.",
    },
  },
  portfolios: {
    columns: {
      company: "Company",
      sector: "Sector",
      price: "Price",
      avgCost: "Avg cost",
      marketPrice: "Market price",
      quantity: "Quantity",
      value: "Market value",
      unrealizedPnl: "Unrealized P&L",
      change: "Change",
      dayChange: "Day %",
      targetPrice: "Target Price",
      halalStatus: "Halal Status",
      ranking: "Ranking",
      delete: "Delete",
      buy: "Buy",
      sell: "Sell",
      closePosition: "Close",
      total: "Total",
    },
    closeConfirm: {
      title: "Close {{ticker}} position?",
      description: "Sell all {{quantity}} shares at the latest market price.",
      ok: "Close",
      cancel: "Cancel",
    },
    pageTitle: "My portfolio",
    holdingsTitle: "Holdings",
    pageSubtitle:
      "Manage your personal halal investments, holdings, and compliance status in one secure dashboard.",
    tutorial: {
      title: "How to use My Portfolio",
      description:
        "Watch a short walkthrough of creating a portfolio, adding stocks, and connecting a broker.",
      iframeTitle: "My Portfolio tutorial",
      gotIt: "Got it",
      watch: "Watch how it works",
    },
    guestAuth: {
      title: "Start your portfolio",
      subtitle:
        "Create a free account to add stocks and connect a broker, or sign in if you already have one.",
    },
    defaultPortfolioName: "My Portfolio 2025",
    badgeBroker: "Broker",
    addSymbol: "Add Symbol",
    buy: "Buy",
    reset: "Reset",
    buyModal: {
      title: "Buy stock",
      subtitle: "Filled at the latest market price. No commission.",
      buy: "Buy",
      buyingPower: "Buying power {{amount}}",
      byShares: "Shares",
      byAmount: "Amount",
      amountLabel: "Amount to spend",
      enterAmount: "Enter amount",
      marketPrice: "Market price",
      approxShares: "Est. shares",
      estimatedCostLabel: "You'll spend",
      cashAfterLabel: "Cash after",
      priceUnavailable:
        "Live price unavailable. Enter a share quantity instead.",
      insufficientFunds: "Not enough cash for this order.",
    },
    sellModal: {
      title: "Sell {{ticker}}",
      subtitle: "Filled at the latest market price. No commission.",
      sell: "Sell",
      cancel: "Cancel",
      held: "Held {{quantity}}",
      estimate: "Est. proceeds {{amount}}",
    },
    resetModal: {
      title: "Reset demo account",
      body: "Cash returns to $10,000. Positions and trade history for this portfolio are removed.",
      confirm: "Reset",
      cancel: "Cancel",
    },
    reconnectModal: {
      title: "Reconnect to trade",
      body: "This brokerage is connected in read-only mode, so orders can't be placed. Reconnect to enable trading.",
      cancel: "Cancel",
    },
    tradeAccess: {
      fallbackBroker: "this broker",
      viewOnlyBadge: "View only",
      viewOnlyTooltip: "This broker shares holdings data only. Buying and selling isn't available here.",
      viewOnlyTitle: "Trading isn't available for {{broker}}",
      viewOnlyDescription: "This brokerage only shares holdings data with Akinda. You can track positions, P&L and halal status here; to buy or sell, use your broker's own app.",
      readOnlyBadge: "Read-only",
      readOnlyTooltip: "This connection can only read data. Reconnect to enable trading.",
      readOnlyTitle: "Connected in read-only mode",
      readOnlyDescription: "{{broker}} supports trading, but this connection only has permission to read data. Reconnect and allow trading to buy and sell from Akinda.",
      reconnect: "Reconnect to trade",
    },
    live: {
      badge: "LIVE",
      buyingPower: "Buying power",
      cash: "Cash",
      equity: "Equity",
      marketValue: "Market value",
      unrealized: "Unrealized P&L",
      positions: "{{count}} positions",
      ordersTitle: "Orders",
      ordersEmpty: "No live orders yet.",
      ticketTitle: "Live order",
      ticketSubtitle: "Sent to your connected brokerage. Real money.",
      orderType: "Order type",
      timeInForce: "Time in force",
      limitPrice: "Limit price",
      stopPrice: "Stop price",
      status: "Status",
      brokerStatus: "Broker status",
      review: "Review order",
      confirm: "Place order",
      back: "Back",
      cancel: "Cancel",
      cancelTitle: "Cancel {{ticker}}?",
      cancelRequested: "Cancel requested.",
      cancelFailed: "Failed to cancel order.",
      submitted: "Order submitted.",
      previewFailed: "Could not preview this order.",
      placeFailed: "Failed to place order.",
      previewUnavailable: "This brokerage does not preview orders. The order will be sent directly.",
      estimatedAmount: "Estimated amount {{amount}}",
      commission: "Estimated commission {{amount}}",
      cashAfter: "Cash after {{amount}}",
      realMoney: "This is a live order. It cannot be undone after the broker accepts it.",
      types: {
        Market: "Market",
        Limit: "Limit",
        Stop: "Stop",
        StopLimit: "Stop limit",
      },
      tif: {
        Day: "Day",
        GTC: "Good til canceled",
      },
      statuses: {
        PENDING_SUBMIT: "Submitting",
        SUBMITTED: "Submitted",
        PENDING: "Working",
        PARTIALLY_FILLED: "Partial",
        FILLED: "Filled",
        CANCELED: "Canceled",
        REJECTED: "Rejected",
        EXPIRED: "Expired",
      },
    },
    trades: {
      title: "Trades",
      empty: "No trades yet.",
      time: "Time",
      side: "Side",
      ticker: "Ticker",
      amount: "Amount",
      realized: "Realized P&L",
      cashAfter: "Cash after",
      deposit: "Deposit",
    },
    addSymbolModal: {
      title: "Add Stocks to Follow",
      subtitle: "Please add stocks for portfolio",
      cancel: "Cancel",
      done: "Done",
      stock: "Stock",
      searchPlaceholder: "Search stocks",
      noStocksFound: "No stocks found",
      searching: "Searching...",
      quantity: "Quantity",
      enterQuantity: "Enter quantity",
      selectPortfolioFirst: "Please select a portfolio first.",
      selectStockFirst: "Please select a stock to add.",
    },
    connectBroker: {
      title: "Connect Broker",
      subtitle: "Securely connect your brokerage account using SnapTrade.",
      iframeTitle: "Connect broker",
      preparing: "Preparing secure broker connection...",
      syncPending: {
        title: "Connection accepted",
        description:
          "Your broker is now syncing your holdings. For most brokers this happens within minutes, but Interactive Brokers can take up to 2 business days. You'll see your positions here as soon as they're ready.",
        done: "Done",
      },
      successMessage: "Broker connected. Holdings will appear once syncing completes.",
      errorMessage: "Failed to connect broker.",
      reconnectTitle: "Reconnect",
      reconnectSubtitle: "Sign in again at your broker to reconnect this account.",
      reconnect: "Reconnect",
    },
    emptyPortfolio: {
      heading: "Create a portfolio",
      title: "You haven't created a portfolio yet.",
      description: "As soon as you create your portfolio, it'll appear here.",
      connectBroker: "Connect Broker",
      createManually: "Create Manually",
    },
    emptyStocks: {
      title: "You haven't added your symbols yet.",
      description: "As soon as you add your symbols, they'll appear here.",
      addSymbol: "Add Symbol",
      ariaLabel: "Empty portfolio symbols",
    },
    brokerSyncing: {
      title: "Your holdings are still syncing",
      descriptionWithBroker:
        "We're waiting for {{brokerName}} to push your positions. This usually takes a few minutes, but Interactive Brokers can take up to 2 business days.",
      description:
        "We're waiting for your broker to push your positions. This usually takes a few minutes, but Interactive Brokers can take up to 2 business days.",
      refresh: "Refresh",
      ariaLabel: "Broker account is syncing",
    },
    brokerRefresh: {
      button: "Refresh",
      success: "Holdings refresh started. Updated positions will appear shortly.",
      error: "Failed to refresh holdings. Please try again.",
    },
    createModal: {
      createTitle: "Create Portfolio",
      updateTitle: "Update Portfolio",
      createSubtitle: "Please enter a name for portfolio",
      updateSubtitle: "Rename the selected portfolio folder",
      cancel: "Cancel",
      create: "Create",
      update: "Update",
      portfolioName: "Portfolio name",
      enterName: "Enter portfolio name",
      nameRequired: "Please enter a portfolio name.",
      description: "Description",
      enterDescription: "Short description for the gallery card",
      coverImage: "Cover image",
      coverUploadTitle: "Click to upload cover",
      coverUploadDescription: "or drag and drop",
      coverUploadHint: "SVG, PNG, JPG or GIF (max. 800x400px)",
      coverReplaceHint: "Click or drag to replace image",
      coverUploadSuccess: "Cover image uploaded successfully",
      coverUploadError: "Failed to upload cover image",
      readyMadeSwitch: "Ready-made portfolio",
      readyMadeSwitchHint:
        "When enabled, this portfolio appears in the Ready-made gallery.",
      createSubtitleGallery:
        "Add a cover image and publish it to the Ready-made gallery",
      updateSubtitleGallery:
        "Update the name, cover image, and gallery visibility",
    },
    deleteModal: {
      title: "Delete Portfolio",
      subtitle:
        'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
      subtitleFallback:
        "Are you sure you want to delete this portfolio? This action cannot be undone.",
      cancel: "Cancel",
      delete: "Delete",
      body: "Deleting this portfolio will remove it from the list permanently. Any associated data will no longer be accessible.",
    },
    shareModal: {
      title: "Share your portfolio",
      subtitle:
        "Turn on sharing to generate a link and allow others to view your portfolio",
      enableSharing: "Enable portfolio sharing",
      shareableLink: "Shareable link",
      copyLink: "Copy link",
      generatingLink: "Generating shareable link...",
      linkCopied: "Link copied",
      copyFailed: "Failed to copy link",
      updateFailed: "Failed to update portfolio sharing settings",
    },
    stats: {
      ariaLabel: "Portfolio statistics",
      portfolio: "Portfolio",
      totalPL: "Total P/L",
      todayChange: "Today Change",
      totalPositions: "Total positions",
      equity: "Equity",
      cash: "Cash",
      unrealized: "Unrealized P&L",
      realized: "Realized {{amount}}",
    },
    halalStatus: {
      halal: "Halal",
      notHalal: "Not halal",
      notQualified: "Not qualified",
    },
    portfolioSelect: {
      yourPortfolios: "Your portfolios",
      addPortfolio: "Add portfolio",
      createManually: "Create Manually",
      connectBroker: "Connect Broker",
      loading: "Loading portfolios...",
      loadError: "Unable to load portfolios",
      empty: "No portfolios or broker accounts available",
    },
    charts: {
      symbolDistribution: "Symbol Distribution",
      sectorAllocation: "Sector Allocation",
      industryAllocation: "Industry Allocation",
      loading: "Loading...",
    },
  },
  trialPromo: {
    eyebrow: "Your free trial is ready",
    daysMark: "{{days}} days",
    free: "free",
    heroLabel: "on any paid plan",
    headline: "Try everything. Pay nothing today.",
    body:
      "Your {{days}}-day free trial is ready. Add a card, use every feature of a paid plan, and cancel anytime before the trial ends.",
    cta: "Start my free trial",
    maybeLater: "Maybe later",
    close: "Close",
  },
  annualDiscountPromo: {
    barAria: "Annual plans are {{percent}}% off",
    barHeadline: "Annual plans are {{percent}}% off.",
    barSub: "Save compared with paying monthly.",
    cta: "See annual plans",
    modalEyebrow: "Limited-time offer",
    modalHeroLabel: "on annual plans",
    modalOff: "off",
    modalHeadline: "Pay yearly. Keep half.",
    modalBody:
      "Yearly billing is {{percent}}% off compared with paying month by month.",
    percentMark: "{{percent}}%",
    compareMonthly: "Monthly",
    compareAnnual: "Annual",
    compareFullPrice: "Full price",
    compareHalfOff: "{{percent}}% off",
    compareVs: "vs",
    maybeLater: "Maybe later",
    close: "Close",
    pageIntro: "Yearly billing is {{percent}}% off compared with monthly.",
  },
  subscriptionResult: {
    successTitle: "Subscription Activated Successfully!",
    planActive: "Your {{planName}} plan is now active.",
    successMessage:
      "Thank you for subscribing. You can now access all features of your plan.",
    failedTitle: "Subscription Failed",
    failedMessage:
      "There was an error processing your subscription. Please try again or contact support.",
    goHome: "Go to Home Page",
    shareTitle: "Share and earn",
    shareDescription:
      "Invite friends with your link. You earn {{percent}}% when they subscribe.",
    shareCta: "Share and earn",
    shareBadge: "{{percent}}%",
    trialTitle: "Your free trial has started!",
    trialActive: "Your {{planName}} trial is active until {{date}}.",
    trialMessage:
      "You have full access now. We will charge ${{amount}} on {{date}} unless you cancel before then.",
  },
  subscriptions: {
    pageTitle: "Subscription Plans",
    noPlansTitle: "No Pricing Plans Available",
    noPlansHint: "Please create pricing plans in the Subscription Matrix",
    featureCategoryDefault: "Shariah Compliance",
    periodMonthly: "monthly",
    periodYearly: "yearly",
    perMonth: "per month",
    perYear: "per year",
    buttons: {
      currentPlan: "Current Plan",
      getStarted: "Get started",
      upgrade: "Upgrade",
      downgrade: "Downgrade",
      manageInApp: "Manage in the app",
      startTrial: "Start {{days}}-day free trial",
    },
    trial: {
      pageIntro:
        "Try any paid plan free for {{days}} days. Add a card, pay nothing today, and cancel anytime before the trial ends.",
      badge: "Free trial",
      dueToday: "Due today",
      firstCharge: "First charge on {{date}}",
      checkoutNote:
        "Your {{days}}-day free trial ends on <strong>{{date}}</strong>. We will charge <strong>${{amount}}</strong> then, and every {{period}} after that, unless you cancel first.",
      startTrial: "Start free trial",
      requiresCard:
        "Add and verify a card to start your free trial. You will not be charged today.",
      activeNote:
        "Your free trial ends on {{date}}. You will be charged {{amount}} then. Cancel anytime before.",
      cancelledNote:
        "Your free trial ends on {{date}}. Auto-renewal is off, so you will not be charged.",
    },
    pastDue: {
      bannerAria: "Payment failed",
      bannerHeadline: "Your payment failed.",
      bannerSub:
        "Update your card to keep your {{plan}} plan, or it will be cancelled soon.",
      updateCard: "Update card",
      retryPayment: "Retry payment",
      retrying: "Retrying…",
      retrySuccess: "Payment successful. Your plan stays active.",
      retryFailed:
        "We still couldn't charge your card. Please update your payment method.",
      retryError: "We couldn't retry the payment right now. Please try again later.",
      badge: "Payment failed",
      cardNote:
        "We couldn't charge your card for your {{plan}} plan. Update your card or retry the payment to keep it, otherwise it will be cancelled soon.",
    },
    billingToggle: {
      monthly: "Monthly",
      annual: "Annual",
      saveUpTo: "Up to {{percent}}% off",
      savePercent: "{{percent}}% off",
    },
    billingEmptyMonthly: "No monthly plans available.",
    billingEmptyAnnual: "No annual plans available.",
    unlimited: "Unlimited",
    checkout: {
      title: "Checkout",
      breadcrumbAria: "Checkout navigation",
      plansBreadcrumb: "Plans",
      addPaymentMethod: "Add new payment method",
      expiry: "Expiry {{date}}",
      defaultLabel: "Default",
      setAsDefault: "Set as default",
      edit: "Edit",
      delete: "Delete",
      editCardTitle: "Edit payment method",
      saveCard: "Save",
      editCardFailed: "Failed to update payment method",
      setDefaultFailed: "Failed to set default payment method",
      deleteCardFailed: "Failed to delete payment method",
      paySubscribe: "Pay & Subscribe",
      recurringCharge:
        "You will be charged <strong>${{amount}}</strong> every {{period}} starting next month. You can cancel anytime.",
    },
    payment: {
      nextPayment: "Next Payment",
      planExpires: "Plan expires: {{date}}",
      paymentDetails: "Payment Details",
      paymentMethod: "Payment Method",
      noPaymentMethod: "No payment method on file",
      cancelCardTitle: "Cancel Subscription",
      cancelCardDesc:
        "If you cancel, your credit card will not be charged in the future.",
      cancelCardCta: "Cancel Subscription",
      activeBadge: "Active",
      perYear: "year",
      perMonth: "month",
      nextBillingDate: "Next billing date: {{date}}.",
      planEndDate: "Your plan will end on {{date}}. Auto-renewal is off.",
      autoRenewOn: "Auto-renewal is on.",
      autoRenewOff: "Auto-renewal is off.",
      changePlan: "Change Plan",
      resumeAutoRenewal: "Resume Auto-Renewal",
      viewFullPlanDetails: "View full plan details",
      editPaymentMethod: "Edit payment method",
      editPaymentDetailsTitle: "Edit payment details",
      confirmPaymentMethod: "Confirm",
      paymentMethodUpdated: "Payment method updated successfully",
      paymentMethodUpdateFailed: "Failed to update payment method",
      freePlanNote: "You're currently on the free plan.",
      storeBilledNote:
        "This subscription is billed through the App Store or Google Play. Manage or cancel it in the Akinda app, or in your store account.",
      manageInStore: "Manage in App Store / Google Play",
      storeCheckoutBlocked:
        "This plan is billed through the App Store or Google Play. Change or cancel it in the app.",
      noActivePlan: "You don't have an active subscription plan yet.",
      selectPaymentMethod: "Select Payment Method",
      addNewCard: "Add New Card",
      activateSubscription: "Activate Subscription",
      defaultCardBadge: "Default",
      expiresOn: "Expires {{date}}",
      planNotFound: "Plan not found",
      noPlanSelected: "No plan selected",
      redirectNoPlan: "No plan selected. Redirecting to subscriptions...",
      cardVerifiedSuccess: "Card verified successfully!",
      cardAddedSuccess: "Card added successfully!",
      backgroundDecorativeAlt: "Decorative background",
    },
    orderSummary: {
      title: "Order summary",
      promocodeLabel: "Promocode",
      promocodePlaceholder: "Enter code",
      apply: "Apply",
      applyPromoAria: "Apply promo code",
      clearPromoAria: "Clear promo code",
      promocodeSuccess: "Promocode was applied successfully",
      promocodeSuccessReferral: "Referral discount applied",
      promocodeInvalid: "Does not exist",
      promocodeReferredOnly: "This promocode is only for referred users",
      promocodeFirstPaidOnly: "This promocode is only for your first paid plan",
      referralBanner:
        "You're getting 10% off because you joined with a referral.",
      annualSubscription: "Annual subscription",
      monthlySubscription: "Monthly subscription",
      discount: "Discount",
      referralDiscount: "Referral discount (10%)",
      total: "Total",
      recurringBilling: "Recurring billing:",
      periodYear: "year",
      periodMonth: "month",
      recurringCharge:
        "You will be charged <strong>${{amount}}</strong> every {{period}} starting today. You can cancel anytime.",
      referralAnnualNote:
        "10% off applies to your first annual charge only. Later renewals are billed at the full plan price.",
      paySubscribe: "Pay & Subscribe",
      backToPlans: "Back to plans",
      missingInfo: "Missing required information",
      activateFailed: "Failed to activate subscription",
    },
    addPaymentMethod: {
      title: "Add new payment method",
      close: "Close",
      uzbekCards: "Uzbekistan Cards",
      uzbekCardsSubtitle: "Uzcard, Humo",
      internationalCards: "International Cards",
      internationalCardsSubtitle: "Visa, Mastercard, American Express",
      cardNumber: "Card number",
      cardNumberRequired: "Please enter card number",
      cardNumberDigits: "Card number must be 13-19 digits",
      cardNumberOnlyDigits: "Card number must contain only digits",
      expiry: "Expiry",
      expiryRequired: "Required",
      expiryFormat: "MM/YY",
      cancel: "Cancel",
      confirm: "Confirm",
    },
    billingHistory: "Billing History",
    billingHistoryTable: {
      invoice: "Invoice",
      amount: "Amount",
      date: "Date",
      status: "Status",
      paymentStatus: "Payment",
      download: "Download",
      downloadInvoice: "Download invoice",
      downloadUnavailable: "Invoice download is not available yet",
      currentPlan: "Current Plan",
      statusPaid: "Paid",
      statusPaymentError: "Payment Error",
      statusAutoRenewalOff: "Auto-Renewal Off",
      statusTrial: "Free trial",
      rowsPerPage: "{{count}} rows",
      previous: "Previous",
      next: "Next",
      pagination: "Pagination",
      loading: "Loading billing history…",
      empty: "No billing history yet",
    },
    table: {
      plan: "Plan",
      fromPlan: "From: {{name}}",
      action: "Action",
      status: "Status",
      date: "Date",
      active: "Active",
      inactive: "Inactive",
    },
    historyActions: {
      activated: "Activated",
      renewed: "Renewed",
      cancelled: "Cancelled",
      trialStarted: "Trial started",
      trialConverted: "Trial converted",
    },
    paginationTotal: "{{from}}-{{to}} of {{total}} records",
    cancelModal: {
      title: "Cancel subscription?",
      subtitle:
        "Your current subscription will stay active until the end of the billing period. After that, it will expire and won't renew automatically.",
      keep: "Keep Subscription",
      confirm: "Yes, cancel",
    },
    messages: {
      userIdMissing: "User ID not found",
      cancelledSuccess: "Subscription cancelled successfully",
      resumedSuccess: "Auto-renewal resumed successfully",
    },
    planNames: {
      investor: "Investor",
      free: "Free",
    },
  },
  articles: {
    title: "Articles",
    addArticle: "Add Article",
    deleteSuccess: "Article deleted successfully",
    deleteError: "Failed to delete article",
    errorLoading: "Error loading articles: {{message}}",
    published: "Published",
    uncategorized: "Uncategorized",
    minRead: "{{count}} min read",
    viewAll: "View all",
    categoriesTitle: "Article Categories",
    selectCategoryPlaceholder: "Select category",
    emptyState: "No articles found",
    back: "← Back",
    copyLink: "Copy link",
    relatedTopics: "Related topics",
    deleteModal: {
      title: "Delete Article",
      subtitle:
        'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
      subtitleFallback:
        "Are you sure you want to delete this article? This action cannot be undone.",
      cancel: "Cancel",
      delete: "Delete",
      body: "Deleting this article will remove it from the list permanently. Any associated data will no longer be accessible.",
    },
    table: {
      title: "Title",
      author: "Author",
      status: "Status",
      readTime: "Read Time",
      createdAt: "Created At",
      actions: "Actions",
      view: "View",
      edit: "Edit",
      delete: "Delete",
    },
  },
  academy: {
    trialLocked:
      "Academy courses are not included in the free trial. Subscribe to unlock them.",
    seo: {
      title: "Halal Investing Courses - Akinda Academy",
      description:
        "Explore halal investing courses at Akinda Academy. Learn Muslim investing, halal stock strategies, and portfolio skills with beginner, intermediate, and advanced lessons in English and Uzbek.",
      keywords:
        "halal investing courses, akinda academy, muslim investing, halal stock investing, islamic finance education, learn halal investing",
    },
    pageTitle: "Halal Investing Courses",
    pageDescription:
      "Learn halal investing with Akinda Academy. Explore courses on Muslim investing, halal stocks, and portfolio strategy—from beginner basics to advanced pro lessons, available in English and Uzbek.",
    authPrompt: {
      title: "Sign in to open courses",
      subtitle:
        "Create an account or sign in to continue in Akinda Academy.",
    },
    levels: {
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced",
      beginnerSubtitle: "Start here with the fundamentals.",
      intermediateSubtitle: "Build on the basics and go deeper.",
      advancedSubtitle: "Master pro-level strategies.",
    },
    coursesCount: "{{count}} courses",
    comingSoon: "Coming soon",
    locked: "Locked",
    startLearning: "Start learning",
    continueLearning: "Continue",
    viewCourseInfo: "View course info",
    reviewCourse: "Review",
    completed: "Completed",
    progressCount: "{{completed}} / {{total}}",
    markComplete: "Mark as complete",
    markIncomplete: "Mark as incomplete",
    completeError: "Could not update topic progress",
    empty: "No courses yet.",
    loadError: "Could not load courses.",
    retry: "Retry",
    notFound: "Course not found.",
    backToAcademy: "Back to Academy",
    createdBy: "Created by {{author}}",
    whatYouLearn: "What you'll learn",
    description: "Description",
    instructor: "Instructor",
    courseContent: "Course content",
    contentSummary: "{{topics}} topics • {{videos}} videos",
    untitledTopic: "Untitled topic",
    videoCount_one: "{{count}} video",
    videoCount_other: "{{count}} videos",
    noVideo: "No video for this topic",
    previous: "Previous",
    next: "Next",
    nowPlaying: "Now playing: {{index}}. {{title}}",
    topicCount_one: "{{count}} topic",
    topicCount_other: "{{count}} topics",
    adminTitle: "My Courses",
    addCourse: "Add Course",
    filterAll: "All levels",
    published: "Published",
    draft: "Draft",
    table: {
      title: "Title",
      level: "Level",
      author: "Author",
      status: "Status",
      order: "Order",
      createdAt: "Created at",
      enrolled: "Enrolled",
      completion: "Completion",
      stats: "Statistics",
      actions: "Actions",
      view: "View",
      edit: "Edit",
      delete: "Delete",
    },
    deleteModal: {
      title: "Delete course",
      subtitle: "Are you sure you want to delete \"{{title}}\"? This cannot be undone.",
      subtitleFallback: "Are you sure you want to delete this course? This cannot be undone.",
      cancel: "Cancel",
      delete: "Delete",
      body: "The course will be removed from Academy for all users.",
      success: "Course deleted",
      error: "Failed to delete course",
    },
    languages: {
      english: "English",
      uzbek: "Uzbek",
      russian: "Russian",
    },
    courses: {
      howToUseAkinda: "How to use Akinda",
      muslimInvesting: "Muslim Investing",
      halalStockInvesting: "Halal Stock Investing",
      moreComingSoon: "More courses coming soon",
      investingPro1: "Investing Pro 1",
      investingPro2: "Investing Pro 2",
    },
    stats: {
      title: "Course statistics",
      backToCourses: "Back to courses",
      loadError: "Could not load statistics.",
      allTime: "All time",
      thisMonth: "This month",
      cards: {
        enrollments: "Enrollments",
        enrollmentsInfo: "Enrollment rows across all courses, drafts included. A learner enrolled in two courses counts twice.",
        learners: "Unique learners",
        learnersInfo: "Distinct users enrolled in at least one course.",
        enrolled: "Enrolled",
        enrolledInfo: "Learners who pressed Start learning, opened a topic, or completed one in this course. All time.",
        started: "Started",
        startedInfo: "Enrolled learners who opened or completed at least one topic.",
        completed: "Completed",
        completedInfo: "Enrolled learners who completed every topic. The percentage is completed divided by enrolled. Adding a topic moves everyone back to in progress.",
        avgProgress: "Average progress",
        avgProgressInfo: "Mean share of topics completed across everyone enrolled.",
        enrolledInPeriod: "New enrollments",
        enrolledInPeriodInfo: "Enrollments that started in the selected dates.",
        activeInPeriod: "Active learners",
        activeInPeriodInfo: "Enrolled learners with any activity (enrolled, opened or completed a topic) in the selected dates.",
      },
      funnel: {
        title: "Topic funnel",
        reached: "Reached",
        completed: "Completed",
        info: "Reached: learners who completed the topic or whose most recently opened topic it is. Completed: learners who marked it complete.",
      },
      monthly: {
        title: "Last 12 months",
        enrolled: "Enrolled",
        completed: "Completed",
      },
      byPlan: {
        title: "By plan",
        plan: "Plan",
        enrolled: "Enrolled",
        completed: "Completed",
      },
      learners: {
        title: "Learners",
        search: "Search by name or email",
        user: "User",
        plan: "Plan",
        status: "Status",
        progress: "Progress",
        lastTopic: "Last topic",
        enrolledAt: "Enrolled",
        lastActivity: "Last activity",
        empty: "No learners yet.",
        statuses: {
          enrolled: "Enrolled",
          in_progress: "In progress",
          completed: "Completed",
        },
      },
    },
  },
  webinars: {
    title: "Investing Webinars",
    categoriesTitle: "Article Categories",
    addWebinar: "Add Webinar",
    loading: "Loading webinars...",
    errorLoading: "Error loading webinars: {{message}}",
    deleteSuccess: "Webinar deleted successfully",
    deleteError: "Failed to delete webinar",
    categories: {
      viewAll: "View all",
      weeklyMarketUpdate: "Weekly Market Update",
      guestSpeaker: "Guest Speaker",
      newUpdates: "New Updates",
      akindaTeam: "Akinda Team",
    },
  },
  news: {
    title: "Market News",
    empty: "No news available at the moment.",
    minRead: "{{count}} min read",
  },
  about: {
    title: "About us",
    eyebrow: "Halal investing platform",
    heroTitle: "Ethical investing, grounded in clarity",
    heroLead:
      "Akinda helps values-driven investors screen, analyze, and monitor global equities using transparent Shariah-aligned criteria, market data, and practical tools—so you can align your portfolio with what matters to you.",
    statsKicker: "Snapshot",
    statsHeading: "At a glance",
    statUsers: "Registered users (May 2026, illustrative)",
    statMarkets: "Mobile app downloads (May 2026, illustrative)",
    statRules: "Equity markets in coverage (illustrative)",
    statUptime: "Platform availability target (illustrative)",
    missionHeading: "Our mission",
    missionText:
      "Make halal and ethical investing accessible: clear screening, honest data, and workflows that respect both faith principles and modern markets.",
    visionHeading: "Our vision",
    visionText:
      "A world where every Muslim and conscientious investor can evaluate opportunities confidently—with the same rigor they expect from conventional finance, without compromising their values.",
    valuesKicker: "Principles",
    valuesHeading: "What guides us",
    valueTransparencyTitle: "Transparency",
    valueTransparencyText:
      "We spell out how names are screened and what data drives each view, so you are never guessing what “halal” means on Akinda.",
    valueEducationTitle: "Education first",
    valueEducationText:
      "Markets move fast; we invest in explainers, labels, and context so screening results are easier to interpret—not just scores on a page.",
    valueIntegrityTitle: "Integrity",
    valueIntegrityText:
      "Product decisions are weighed against user trust: we avoid hype, disclose limits, and improve screening as standards and data evolve.",
    valueCommunityTitle: "Global community",
    valueCommunityText:
      "From retail investors to analysts, we build for a diverse audience united by a simple idea: invest with conviction and conscience.",
    offeringsKicker: "Platform",
    offeringsHeading: "What you can do with Akinda",
    offeringScreenTitle: "Shariah-aligned screening",
    offeringScreenText:
      "Filter and compare securities with rulesets inspired by commonly accepted Islamic finance principles—paired with issuer and sector context.",
    offeringAnalysisTitle: "Analysis & market context",
    offeringAnalysisText:
      "Pair halal status with fundamentals, news, and tools that help you understand risk and opportunity—not just a binary label.",
    offeringPortfolioTitle: "Portfolios & monitoring",
    offeringPortfolioText:
      "Organize ideas, watch positions, and revisit decisions as filings and prices change—so your plan stays intentional over time.",
    milestonesKicker: "Journey",
    milestonesHeading: "Company milestones",
    milestonePeriod1: "Apr 2025",
    milestonePeriod2: "Dec 2025",
    milestonePeriod3: "Mar–Apr 2026",
    milestonePeriod4: "May 2026",
    milestonePeriod5: "Jun 2026",
    m1: "Akinda was founded around a clear idea: build transparent, Shariah-aligned tools that help more people invest with conviction.",
    m2: "First platform launch — core screening, issuer context, and market data went live for early members.",
    m3: "Mobile apps and a B2B product launch — invest, monitor, and partner with Akinda on the go.",
    m4: "Growth snapshot: 5,000+ users and 2,000+ app downloads as adoption accelerates.",
    m5: "Targeting 10,000+ users while expanding education, coverage, and platform reliability.",
    ctaHeading: "Explore how we screen stocks",
    ctaText:
      "See the methodology behind our halal checks and how we combine data with Islamic finance criteria.",
    ctaButton: "Open screening",
  },
  auth: {
    login: {
      back: "Back",
      title: "Welcome Back",
      subtitle: "Welcome back! Please enter your details.",
      email: "Email",
      password: "Password",
      emailPlaceholder: "Enter your email",
      passwordPlaceholder: "Enter your password",
      emailRequired: "Please input your email!",
      emailInvalid: "Please enter a valid email address",
      passwordRequired: "Password is required",
      remember30Days: "Remember for 30 days",
      forgotPassword: "Forgot password",
      signIn: "Sign In",
      signingIn: "Signing in...",
      or: "or",
      signInWithGoogle: "Sign in with Google",
      signInWithApple: "Sign in with Apple",
      noAccount: "Don't have an account?",
      signUpLink: "Sign up",
      success: "Login successful! Welcome back!",
      error: "Login failed. Please check your credentials and try again.",
    },
    errors: {
      invalidCredentials:
        "Login failed. Please check your email and password and try again.",
      emailAlreadyExists: "An account with this email already exists.",
      emailNotVerified: "Please verify your email before signing in.",
      accountLocked:
        "Your account has been locked. Please try again later or contact support.",
      userNotFound: "No account found with this email.",
      tooManyAttempts: "Too many failed attempts. Please try again later.",
      invalidEmail: "Please enter a valid email address.",
      weakPassword: "Password does not meet the requirements.",
      phoneAlreadyExists: "This phone number is already in use.",
    },
    signUp: {
      back: "Back",
      title: "Sign up",
      name: "Name",
      namePlaceholder: "Enter your name",
      nameRequired: "Please input your name!",
      emailPlaceholder: "Enter your email",
      passwordPlaceholder: "Create a password",
      passwordMin: "Must be at least 8 characters",
      passwordSpecial: "Must contain one special character (!@#$%^&*?/)",
      passwordUpper: "Must contain one uppercase letter",
      passwordLower: "Must contain one lowercase letter",
      passwordNumber: "Must contain one number",
      creatingAccount: "Creating account...",
      getStarted: "Get started",
      or: "or",
      signUpWithGoogle: "Sign up with Google",
      signUpWithApple: "Sign up with Apple",
      hasAccount: "Already have an account?",
      logInLink: "Log in",
      success: "Registration successful! Please verify your email.",
      errorGeneric: "Registration failed. Please try again.",
    },
  },
  personalInfo: {
    userIdNotFound: "User ID not found",
    updateSuccess: "Personal information updated successfully",
    updateError: "Failed to update personal information",
    loadingUserData: "Loading user data...",
    errorLoadingUserData: "Error loading user data",
    userNotFound: "User not found",
    userNotFoundDesc: "The requested user could not be found.",
    title: "Personal info",
    subtitle: "Update photo and personal details.",
    completeProfileTitle: "Complete your profile",
    completeProfileDesc:
      "Please select your country. Phone number is required for Uzbekistan, Kazakhstan, and Kyrgyzstan.",
    saveChanges: "Save changes",
    navLabel: "Account sections",
    navAccount: "Account",
    navCommunity: "Community",
    navPassword: "Password",
    accountTitle: "Account Settings",
    communityTitle: "Community profile",
    passwordTitle: "Password",
    profileImage: "Profile image",
    editPhoto: "Edit",
    uploadPhoto: "Upload photo",
    uploadingPhoto: "Uploading…",
    required: "Required",
    changePasswordTitle: "Change Password",
    changePasswordSubtitle: "Update your protection.",
    changePasswordButton: "Change Password",
    cancel: "Cancel",
    form: {
      firstName: "First Name",
      enterFirstName: "Enter first name",
      firstNameRequired: "Please input your first name!",
      lastName: "Last Name",
      enterLastName: "Enter last name",
      lastNameRequired: "Please input your last name!",
      missingFieldsTitle: "Please complete required fields",
      fillRequiredFields: "Please fill in all required fields.",
      ok: "OK",
      email: "Email Address",
      emailRequired: "Please input your email!",
      emailInvalid: "Please enter a valid email address",
      country: "Country",
      selectCountry: "Select your country",
      countryRequired: "Please select your country",
      phone: "Phone number",
      phoneRequired: "Please enter your phone number",
      phoneInvalid: "Please enter a valid phone number for the selected country",
      phonePlaceholderRequired: "Enter phone number",
      phonePlaceholderOptional: "Phone number (optional)",
      avatar: "Avatar",
      avatarPreviewAlt: "Avatar preview",
      uploadAvatarTitle: "Click to upload avatar",
      uploadAvatarDescription: "or drag and drop",
      uploadAvatarHint: "SVG, PNG, JPG or GIF (max. 800×400px)",
      uploadAvatarReplaceHint: "Click or drag to replace avatar",
      avatarUploadSuccess: "Avatar uploaded successfully",
      avatarUploadError: "Failed to upload avatar",
      tags: "Tags",
      selectTags: "Select tags",
      role: "Role",
      selectRole: "Select a role",
      rolesLoadError: "Error loading roles",
      roleRequired: "Please select a role!",
      company: "Company",
      enterCompany: "Enter company name",
      bio: "Bio",
      enterBio: "Enter bio",
      note: "Note",
      charactersLeft: "{{count}} characters left",
      password: "Password",
      passwordRequired: "Password is required",
      repeatPassword: "Repeat Password",
      repeatPasswordRequired: "Please confirm your password!",
      passwordsMismatch: "The two passwords do not match!",
      communityProfile: "Community Profile",
      username: "Username",
      usernameTooltip: "Your unique username for the community",
      usernameRequired: "Please input your username!",
      usernamePattern: "Username must be 3-30 characters long and contain only lowercase letters, numbers, and underscores",
      usernamePlaceholder: "username",
      coverImage: "Cover Image",
      uploadCoverTitle: "Click to upload cover image",
      uploadCoverDescription: "or drag and drop",
      uploadCoverHint: "SVG, PNG, JPG or GIF (max. 1200×400px)",
      uploadCoverReplaceHint: "Click or drag to replace cover image",
      coverPreviewAlt: "Cover image preview",
      coverUploadSuccess: "Cover image uploaded successfully",
      coverUploadError: "Failed to upload cover image",
      portfolioPublic: "Public Portfolio",
      portfolioPublicTooltip: "Allow others to view your portfolio in the community",
    },
    tagOptions: {
      frontend: "Frontend",
      backend: "Backend",
      design: "Design",
      management: "Management",
    },
    passwordChange: {
      oldPassword: "Old Password",
      oldPasswordRequired: "Please input your old password!",
      newPassword: "New Password",
      newPasswordRequired: "Please input your new password!",
      confirmNewPassword: "Confirm New Password",
      confirmRequired: "Please confirm your new password!",
      passwordsMismatch: "The two passwords do not match!",
      checkMinLength: "Must be at least 8 characters",
      checkSpecial: "Must contain one special character",
      checkUpper: "Must contain one uppercase letter",
      checkNumber: "Must contain one number",
      requirementsError:
        "Password must meet all requirements: at least 8 characters, one special character, one uppercase letter, and one number.",
      success: "Password changed successfully",
      errorGeneric:
        "Failed to change password. Please check your old password.",
      currentPassword: "Current password",
      newPasswordLabel: "New password",
      verifyPassword: "Verify password",
      forgotPassword: "Forgot your password?",
      resetSent: "Reset instructions sent to your email.",
      resetError: "Failed to send reset instructions. Please try again.",
    },
  },
  cards: {
    pageTitle: "My Cards",
    subtitle:
      "Manage your payment cards and set default card for subscriptions",
    emptyTitle: "No cards added yet",
    emptyDescription:
      "Add your first payment card to start using subscription services",
    addFirstCard: "Add Your First Card",
    addCard: "Add Card",
    expires: "Expires {{date}}",
    defaultTag: "Default",
    active: "Active",
    inactive: "Inactive",
    edit: "Edit",
    setDefault: "Set as default",
    delete: "Delete",
    deleteCard: "Delete Card",
    deleteConfirmLine: "Are you sure you want to delete this card?",
    deleteDefaultWarning:
      "This is your default card. You may want to set another card as default first.",
    deleteConfirm:
      "Are you sure you want to delete this card? This action cannot be undone.",
    deleteOk: "Yes, Delete",
    cancel: "Cancel",
    modalAddTitle: "Add New Card",
    modalEditTitle: "Edit Card",
    secureHint: "Your card details are encrypted and secure",
    smsVerificationInfo:
      "Your card will be verified via SMS. Make sure your phone number is up to date.",
    cardholderName: "Cardholder Name",
    cardholderNamePlaceholder: "John Doe",
    cardHolderTooltip: "Enter the name as it appears on your card",
    cardholderRequired: "Please enter cardholder name!",
    cardholderMin: "Cardholder name must be at least 2 characters",
    cardholderMax: "Cardholder name must not exceed 50 characters",
    cardNumber: "Card Number",
    cardNumberPlaceholder: "1234 5678 9012 3456",
    cardNumberTooltip: "Enter your 13-19 digit card number",
    cardNumberRequired: "Please enter card number!",
    cardNumberLength: "Card number must be between 13 and 19 digits!",
    cardNumberDigits: "Card number must contain only digits!",
    expiryDate: "Expiry Date",
    expiryPlaceholder: "MM/YY",
    expiryTooltip: "Enter the expiration date as shown on your card",
    expiryRequired: "Please enter expiry date!",
    expiryInvalid: "Please enter a valid expiry date (MM/YY)!",
    setDefaultCard: "Set as Default Card",
    defaultCardTooltip:
      "Default card will be used automatically for new subscriptions",
    updateCard: "Update Card",
    addCardSubmit: "Add Card",
    otpTitle: "Verify Your Card",
    otpPhonePlaceholder: "your phone",
    missingVerification: "Missing verification data. Please try again.",
  },
  notifications: {
    title: "Notifications",
    markAllRead: "Mark all as read",
    loading: "Loading notifications...",
    empty: "No notifications",
    drawerTitle: "Notification",
    close: "Close",
    loadingDetail: "Loading notification details...",
    markAllSuccess: "All notifications marked as read",
    markAllError: "Failed to mark all as read",
  },
  lockDisplay: {
    loginPrompt: "Please login to access this content",
    loginButton: "Login",
    watchDemo: "Watch Demo",
    upgradePrompt: "You have reached your limit, upgrade to get more",
    upgradeButton: "Upgrade Now",
    trialPrompt: "Try it free for 14 days. No charge today.",
    trialHint: "You can try it free for 14 days, with no charge today.",
    trialButton: "Start free trial",
    cancel: "Cancel",
    reportAccessDeniedTitle: "Access to {{stock}} is locked",
    reportAccessDeniedTitleGeneric: "This report is locked",
    reportAccessDeniedWithPlan:
      "Upgrade to the {{plan}} plan to access {{category}} analyses.",
    reportAccessDeniedPlanOnly:
      "Upgrade to the {{plan}} plan to access this stock analysis report.",
    reportAccessDenied:
      "Upgrade your subscription to access this stock analysis report.",
    categoryAccessDeniedTitle: "{{category}} analyses are locked",
    categoryAccessDeniedWithPlan:
      "Upgrade to the {{plan}} plan to unlock {{category}} stock analyses.",
    categoryAccessDenied:
      "Upgrade your subscription to access {{category}} stock analyses.",
  },
  community: {
    feed: {
      title: "Community",
      createPost: "Create Post",
      search: "Search",
      myProfile: "My Profile",
      forYou: "For You",
      following: "Following",
      saved: "Saved",
      noPosts: "No posts yet",
      errorLoading: "Error loading feed",
      loadMore: "Load More",
      suggestedUsers: "Suggested users to follow",
      usernameRequired: "Please set a username in Personal Info first",
      userDataError: "Failed to load your profile",
    },
    post: {
      back: "Back",
      comments: "Comments",
      comment: "Comment",
      reply: "Reply",
      cancel: "Cancel",
      replyingTo: "Replying to",
      commentPlaceholder: "Write a comment...",
      commentRequired: "Comment cannot be empty",
      noComments: "No comments yet. Be the first to comment!",
      notFound: "Post not found",
      justNow: "Just now",
      minutesAgo: "{{count}}m ago",
      hoursAgo: "{{count}}h ago",
      daysAgo: "{{count}}d ago",
      imageLoadError: "Image failed to load",
      likeAria: "Like, {{count}} likes",
      commentAria: "Comments, {{count}} comments",
      saveAria: "Save post",
      shareAria: "Share post",
      likeError: "Failed to like post",
      saveError: "Failed to save post",
      sharError: "Failed to share post",
      linkCopied: "Link copied to clipboard",
      shareTitle: "Check out this post",
      delete: "Delete Post",
      report: "Report Post",
      deleteConfirmTitle: "Delete this post?",
      deleteConfirmContent: "This action cannot be undone.",
      deleteConfirmOk: "Delete",
      deleteConfirmCancel: "Cancel",
      deleteSuccess: "Post deleted",
      deleteError: "Failed to delete post",
      reportTitle: "Report this post?",
      reportContent: "We'll review this post and take appropriate action.",
      reportOk: "Report",
      reportCancel: "Cancel",
      reportSuccess: "Post reported",
      reportError: "Failed to report post",
    },
    comment: {
      delete: "Delete Comment",
      reply: "Reply",
      justNow: "Just now",
      minutesAgo: "{{count}}m ago",
      hoursAgo: "{{count}}h ago",
      daysAgo: "{{count}}d ago",
      likeError: "Failed to like comment",
      deleteConfirmTitle: "Delete this comment?",
      deleteConfirmContent: "This action cannot be undone.",
      deleteConfirmOk: "Delete",
      deleteConfirmCancel: "Cancel",
      deleteSuccess: "Comment deleted",
      deleteError: "Failed to delete comment",
    },
    user: {
      follow: "Follow",
      following: "Following",
      followers: "{{count}} followers",
      followSuccess: "User followed",
      followError: "Failed to follow user",
      unfollowSuccess: "User unfollowed",
      unfollowError: "Failed to unfollow user",
    },
    profile: {
      back: "Back",
      notFound: "Profile not found",
      posts: "Posts",
      portfolio: "Portfolio",
      followers: "Followers",
      follow: "Follow",
      following: "Following",
      block: "Block User",
      unblock: "Unblock User",
      blockedUsers: "Blocked Users",
      editProfile: "Edit Profile",
      postsCount: "{{count}} posts",
      followersCount: "{{count}} followers",
      noPosts: "No posts yet",
      noFollowers: "No followers yet",
      noFollowing: "Not following anyone yet",
      portfolioNotShared: "Portfolio not shared",
      portfolioTitle: "Public Portfolio",
      holdingsCount: "{{count}} holdings",
      halalPercent: "{{percent}}% Halal",
      halalStatus: {
        halal: "Halal",
        not_halal: "Not Halal",
        doubtful: "Doubtful",
        unrated: "Unrated",
        unknown: "Unknown",
      },
    },
    search: {
      title: "Search",
      back: "Back",
      placeholder: "Search posts and people...",
      posts: "Posts",
      people: "People",
      emptyState: "Search for posts and people",
      noPostsFound: "No posts found",
      noPeopleFound: "No people found",
      suggestedUsers: "Suggested users",
    },
    createPost: {
      title: "Create Post",
      placeholder: "What's on your mind?",
      addImage: "Add Image",
      post: "Post",
      bodyRequired: "Post cannot be empty",
      bodyTooLong: "Post is too long",
      maxImages: "Maximum {{max}} images allowed",
      invalidFileType: "Only PNG, JPG, JPEG, and WEBP images are allowed",
      fileTooLarge: "File size must be less than 10MB",
      success: "Post created",
      error: "Failed to create post",
    },
    blocked: {
      title: "Blocked Users",
      back: "Back",
      noBlockedUsers: "No blocked users",
      unblock: "Unblock",
    },
  },
  staticPages,
  investingCalculator,
  zakatCalculator,
  purificationCalculator,
  articleSignupPrompt,
} as const;
